<script setup lang="ts">
import {computed, ref} from 'vue'
import {useLocalization} from '@hantera/portal-app'
import {ElasticInput, TextInput, useClose} from "@hantera/design-system";
import {IamApiError} from "@/backend/iam.types";
import {useIamEventsStore} from '@/stores/iam/iam-events-store'

const props = defineProps<{
  onCreated?: (role: any) => void
  onCancel?: () => void
  iamService: any
}>()

const t = useLocalization('iam.roles')
const close = useClose()
const iamEvents = useIamEventsStore()

const key = ref('')
const description = ref('')
const isCreating = ref(false)
const errorMessage = ref('')

const isValid = computed(() => {
  if (!key.value.trim()) return false

  // Validate role key format
  const keyPattern = /^[a-zA-Z0-9_:-]+$/
  if (!keyPattern.test(key.value)) return false

  // Cannot start with system:
  if (key.value.startsWith('system:')) return false

  // Max length
  if (key.value.length > 100) return false

  return true
})

async function create() {
  if (!isValid.value) return

  errorMessage.value = ''
  isCreating.value = true

  try {
    const role = await props.iamService.createRole(
      key.value.trim(),
      description.value.trim() || undefined
    )

    // Broadcast to all RolesView instances
    iamEvents.emitRolesListChanged()

    props.onCreated?.(role)
    close()
  } catch (error: any) {

    // Backend returns: { errors: [{ code, message, details }] }
    if (error instanceof IamApiError) {
      const errorCode = error.code
      const apiMessage = error.message

      // Try to get localized message for this error code
      const localizedKey = `create.errors.${errorCode}`
      const localizedMsg = t(localizedKey)

      // If localization exists, use it; otherwise use API message
      if (localizedMsg !== localizedKey) {
        errorMessage.value = localizedMsg
      } else {
        errorMessage.value = apiMessage ?? errorCode
      }
    } else {
      errorMessage.value = t('create.errors.UNKNOWN')
    }
    console.error('Failed to create role:', error)
  } finally {
    isCreating.value = false
  }
}

function cancel() {
  props.onCancel?.()
}
</script>

<template>
  <div class="create-role p2">
    <div class="form-field">
      <label>{{ t('create.form.key') }} *</label>
      <TextInput
        v-model="key"
        :placeholder="t('create.form.keyPlaceholder')"
        @keydown.enter="isValid && !isCreating && create()"
      />
      <p class="field-help">{{ t('create.form.keyHelp') }}</p>
    </div>

    <div class="form-field">
      <label>{{ t('create.form.description') }}</label>
      <ElasticInput
        v-model="description"
        :placeholder="t('create.form.descriptionPlaceholder')"
        :maxlength="500"
      />
    </div>

    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="dialog-actions">
      <button @click="cancel" class="btn-secondary" :disabled="isCreating">
        {{ t('actions.cancel') }}
      </button>
      <button @click="create" class="btn-primary" :disabled="!isValid || isCreating">
        {{ isCreating ? t('actions.creating') : t('actions.create') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.create-role {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 400px;
}

.error-message {
  color: var(--color-error);
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
</style>
